import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Search, ScanEye, FileWarning, HandCoins, ArrowRight } from "lucide-react";

const steps = [
  {
    icon: Search,
    title: "Free reputation audit",
    body: "Share your Google Business Profile. We analyze every negative review against Google's content policies — line by line.",
  },
  {
    icon: ScanEye,
    title: "We identify violators",
    body: "Fake reviews, off-topic rants, conflicts of interest, harassment, spam — we flag every review that qualifies for removal.",
  },
  {
    icon: FileWarning,
    title: "We file & escalate",
    body: "We submit through Google's official reporting channels, follow up, and escalate cases that stall — until the review is gone.",
  },
  {
    icon: HandCoins,
    title: "You pay only after removal",
    body: "$195 per successfully removed review. If a review can't be removed, you owe nothing. Simple. Fair. Guaranteed.",
  },
];

export default function HowItWorks() {
  const lineRef = useRef(null);
  const lineInView = useInView(lineRef, { once: true, amount: 0.4 });

  return (
    <section id="how" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            How it works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
            From free audit to a cleaner profile —{" "}
            <span className="text-white/50">in four ethical steps.</span>
          </h2>
        </div>

        <div ref={lineRef} className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* animated connecting line */}
          <div className="hidden lg:block absolute left-6 right-6 top-14 h-px pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <motion.div
              initial={{ scaleX: 0 }}
              animate={lineInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "left" }}
              className="absolute inset-0 bg-gradient-to-r from-[#10B981] via-[#D4AF37] to-[#10B981]"
            />
          </div>

          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-white/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <motion.div
                  whileHover={{ scale: 1.05, rotate: -3 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="w-11 h-11 grid place-items-center rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 relative"
                >
                  <s.icon className="w-5 h-5 text-[#10B981]" />
                  {/* pulsing dot on connector position */}
                  <span className="hidden lg:block absolute -right-[38px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#10B981] shadow-[0_0_10px_#10B981]" />
                </motion.div>
                <div className="font-display text-4xl font-bold text-white/10 tabular-nums group-hover:text-white/20 transition-colors">
                  0{i + 1}
                </div>
              </div>
              <h3 className="mt-5 font-display font-semibold text-lg text-white leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.body}</p>

              {/* subtle arrow between cards */}
              {i < steps.length - 1 && (
                <ArrowRight className="hidden lg:block absolute top-8 -right-3 w-4 h-4 text-white/25" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
